import React, { useEffect, useState, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Plus, Trash2, ImagePlus, Upload, Edit3, Eye, Filter, Download, Move } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { EQUIPMENT, EQUIPMENT_GROUPS, equipmentMeta, pinStatusFromTelemetry, STATUS_BG, STATUS_RING } from "../components/acms/Equipment";
import { Badge } from "../components/ui/badge";

export default function FloorPlans() {
  const { sites, selectedSite } = useSiteContext();
  const [floors, setFloors] = useState([]);
  const [activeFloor, setActiveFloor] = useState(null);
  const [tele, setTele] = useState(null);
  const [devices, setDevices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ floor_number: 1, name: "", image_url: "" });
  const [dragging, setDragging] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pinDevice, setPinDevice] = useState("");
  const [pinDeviceType, setPinDeviceType] = useState("smoke_detector");
  const [showPinPicker, setShowPinPicker] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [activePin, setActivePin] = useState(null);
  const [hiddenGroups, setHiddenGroups] = useState(new Set());
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [exporting, setExporting] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!selectedSite) return;
    const r = await api.get("/floors", { params: { site_id: selectedSite } });
    const sorted = r.data.slice().sort((a, b) => (a.floor_number ?? 0) - (b.floor_number ?? 0));
    setFloors(sorted);
    if (sorted.length && (!activeFloor || !sorted.find((f) => f.floor_id === activeFloor.floor_id))) {
      setActiveFloor(sorted[0]);
    } else if (!sorted.length) setActiveFloor(null);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite) return;
    const fetch = () => {
      api.get(`/telemetry/${selectedSite}`).then((r) => setTele(r.data)).catch(() => {});
      api.get("/devices", { params: { site_id: selectedSite } }).then((r) => setDevices(r.data)).catch(() => {});
    };
    fetch();
    const i = setInterval(fetch, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  useEffect(() => { setEditMode(false); setActivePin(null); }, [activeFloor?.floor_id]);

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1800;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Please drop a JPEG, PNG, or PDF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
    try {
      if (file.type === "application/pdf") {
        // Store PDF as base64 data URL directly (rendered in <embed>)
        const reader = new FileReader();
        reader.onload = (e) => setForm((f) => ({ ...f, image_url: e.target.result }));
        reader.readAsDataURL(file);
        toast.success("PDF ready — click Save to upload");
      } else {
        const dataUrl = await compressImage(file);
        setForm((f) => ({ ...f, image_url: dataUrl }));
        toast.success("Image ready — click Save to upload");
      }
    } catch { toast.error("Failed to read file"); }
  };

  const addFloor = async () => {
    try {
      if (!form.image_url) return toast.error("Upload an image/PDF or paste a URL");
      await api.post("/floors", { ...form, site_id: selectedSite });
      toast.success("Floor plan added");
      setOpen(false); setForm({ floor_number: 1, name: "", image_url: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleImgClick = (e) => {
    if (!activeFloor || !editMode || draggingIdx !== null) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setPinDevice("");
    setShowPinPicker(true);
  };

  // Drag-to-reposition pin handlers
  const onPinMouseDown = (e, idx) => {
    if (!editMode) return;
    e.stopPropagation();
    e.preventDefault();
    setDraggingIdx(idx);
  };
  const onCanvasMouseMove = (e) => {
    if (draggingIdx === null || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setActiveFloor((f) => {
      if (!f) return f;
      const pins = f.pins.map((p, i) => (i === draggingIdx ? { ...p, x, y } : p));
      return { ...f, pins };
    });
  };
  const onCanvasMouseUp = async () => {
    if (draggingIdx === null) return;
    try {
      await api.patch(`/floors/${activeFloor.floor_id}`, { pins: activeFloor.pins });
      toast.success("Pin moved");
    } catch {
      toast.error("Save failed");
      load();
    } finally {
      setDraggingIdx(null);
    }
  };

  const exportPng = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(canvasRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) => !(node?.classList?.contains?.("no-export")),
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${activeFloor.name.replace(/\s+/g, "_")}_pins.png`;
      a.click();
      toast.success("PNG exported");
    } catch (e) {
      toast.error("Export failed: " + e.message?.slice(0, 60));
    } finally {
      setExporting(false);
    }
  };

  const savePin = async () => {
    if (!pinDevice) return toast.error("Select a device");
    const dev = devices.find((d) => d.device_id === pinDevice);
    const newPin = {
      ...pendingPin,
      device_id: pinDevice,
      device_name: dev?.device_name,
      device_type: dev?.device_type || pinDeviceType,
    };
    const updated = [...(activeFloor.pins || []), newPin];
    await api.patch(`/floors/${activeFloor.floor_id}`, { pins: updated });
    toast.success("Pin placed");
    setShowPinPicker(false); setPendingPin(null); setPinDevice("");
    load();
  };

  const deletePin = async (idx) => {
    if (!window.confirm("Delete this pin?")) return;
    const updated = activeFloor.pins.filter((_, i) => i !== idx);
    await api.patch(`/floors/${activeFloor.floor_id}`, { pins: updated });
    toast.success("Pin removed"); setActivePin(null); load();
  };

  const deleteFloor = async () => {
    if (!activeFloor) return;
    if (!window.confirm(`Delete "${activeFloor.name}" floor plan?`)) return;
    await api.delete(`/floors/${activeFloor.floor_id}`);
    toast.success("Floor plan deleted");
    setActiveFloor(null);
    load();
  };

  // Filter devices for pin picker by selected type
  const devicesByType = useMemo(() => {
    const already = new Set((activeFloor?.pins || []).map((p) => p.device_id));
    return devices.filter((d) => d.device_type === pinDeviceType && !already.has(d.device_id));
  }, [devices, pinDeviceType, activeFloor]);

  const toggleGroup = (g) => {
    const next = new Set(hiddenGroups);
    next.has(g) ? next.delete(g) : next.add(g);
    setHiddenGroups(next);
  };

  // Counts by group for legend chips
  const groupCounts = useMemo(() => {
    const counts = {};
    (activeFloor?.pins || []).forEach((p) => {
      const meta = equipmentMeta(p.device_type);
      counts[meta.group] = (counts[meta.group] || 0) + 1;
    });
    return counts;
  }, [activeFloor]);

  const isPdf = activeFloor?.image_url?.startsWith("data:application/pdf");

  return (
    <div className="space-y-5" data-testid="floors-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → {sites.find((s) => s.site_id === selectedSite)?.name} → Floor Plans</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Floor Plans</h1>
          <p className="text-sm text-gray-500 mt-1">Architectural blueprints with live equipment pins · pins colour-code on/off/faulty from telemetry</p>
        </div>
        <div className="flex items-center gap-2">
          {activeFloor && (
            <Button onClick={exportPng} disabled={exporting} variant="outline" className="border-white/15 hover:bg-gray-100" data-testid="export-png-btn">
              <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> {exporting ? "Exporting…" : "Export PNG"}
            </Button>
          )}
          {activeFloor && (
            <Button
              onClick={() => setEditMode((e) => !e)}
              className={editMode ? "bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30" : "bg-white/10 hover:bg-white/15"}
              data-testid="edit-mode-btn"
            >
              {editMode ? <><Eye className="w-4 h-4 mr-1" strokeWidth={2.5} /> Exit Edit</> : <><Edit3 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Edit Mode</>}
            </Button>
          )}
          <Button onClick={() => setOpen(true)} className="f360-gradient border-0" data-testid="add-floor-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Floor Plan
          </Button>
        </div>
      </div>

      {/* Floor tabs */}
      {floors.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {floors.map((f) => (
            <button key={f.floor_id} onClick={() => setActiveFloor(f)}
              data-testid={`floor-tab-${f.floor_id}`}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap ${activeFloor?.floor_id === f.floor_id ? "f360-gradient text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
              {f.floor_number === 0 ? "Basement" : `Floor ${f.floor_number}`} — {f.name}
              <span className="ml-2 text-[10px] opacity-70">{f.pins?.length || 0} pins</span>
            </button>
          ))}
        </div>
      )}

      {editMode && (
        <div className="glass border border-orange-500/30 bg-orange-500/5 rounded-lg p-3 text-xs text-orange-200 flex items-center gap-2 no-export">
          <Edit3 className="w-4 h-4" strokeWidth={2.5} /> <b>EDIT MODE</b> — click empty spot to add a pin · drag any pin to reposition · click pin to view / remove.
        </div>
      )}

      {/* Equipment legend / filter */}
      {activeFloor && (
        <div className="glass rounded-lg p-3 flex flex-wrap items-center gap-2 no-export" data-testid="equipment-legend">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1 mr-2"><Filter className="w-3 h-3" strokeWidth={2.5} /> SHOW</span>
          {EQUIPMENT_GROUPS.map((g) => {
            const hidden = hiddenGroups.has(g.key);
            const count = groupCounts[g.key] || 0;
            return (
              <button
                key={g.key}
                onClick={() => toggleGroup(g.key)}
                data-testid={`legend-${g.key}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${hidden ? "bg-gray-50 border-gray-200 text-gray-500" : "border-white/20 text-gray-900"}`}
                style={hidden ? {} : { background: `${g.color}22`, borderColor: `${g.color}66` }}
              >
                <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: g.color }}></span>
                {g.label} <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-3 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> OK</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Warn</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> Fault</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500"></span> Off</span>
          </div>
        </div>
      )}

      {activeFloor ? (
        <div className="glass rounded-lg overflow-hidden" ref={canvasRef}>
          <div
            className={`relative ${editMode ? "cursor-crosshair" : "cursor-default"} overflow-auto bg-slate-200`}
            onClick={handleImgClick}
            onMouseMove={onCanvasMouseMove}
            onMouseUp={onCanvasMouseUp}
            onMouseLeave={onCanvasMouseUp}
            style={{ touchAction: "pinch-zoom" }}
            data-testid="floor-canvas"
          >
            {isPdf ? (
              <embed src={activeFloor.image_url} type="application/pdf" className="w-full" style={{ height: "70vh" }} />
            ) : (
              <img ref={imgRef} src={activeFloor.image_url} alt="Floor plan" className="w-full max-h-[78vh] object-contain bg-slate-100 select-none" draggable={false} />
            )}
            {!isPdf && (activeFloor.pins || []).map((p, i) => {
              const meta = equipmentMeta(p.device_type);
              if (hiddenGroups.has(meta.group)) return null;
              const st = pinStatusFromTelemetry(p.device_type, tele, p.device_name);
              const Icon = meta.icon;
              const isDragging = draggingIdx === i;
              return (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); if (!isDragging) setActivePin({ ...p, idx: i, meta, status: st }); }}
                  onMouseDown={(e) => onPinMouseDown(e, i)}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                  data-testid={`pin-${i}`}
                  title={`${meta.label} · ${p.device_name || ""} · ${st.label}${editMode ? " · drag to reposition" : ""}`}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-2 border-white ${STATUS_BG[st.code]} ring-4 ${STATUS_RING[st.code]} flex items-center justify-center shadow-lg transition ${editMode ? "cursor-grab active:cursor-grabbing" : "hover:scale-125"} ${isDragging ? "scale-125 z-20" : ""}`}
                >
                  <Icon className="w-4 h-4 text-gray-900 pointer-events-none" />
                </button>
              );
            })}
          </div>
          <div className="p-3 flex items-center justify-between gap-2 text-xs border-t border-gray-200 no-export">
            <div className="text-gray-500 flex items-center gap-2 flex-wrap">
              <span>{activeFloor.pins?.length || 0} pins · live polling 5s</span>
              <span className="text-slate-600">·</span>
              <span className="text-gray-500">{editMode ? <span className="flex items-center gap-1"><Move className="w-3 h-3" strokeWidth={2.5} /> Drag pins to reposition</span> : "Click a pin for details"}</span>
            </div>
            <Button size="sm" variant="ghost" onClick={deleteFloor} className="text-red-400 hover:text-red-300 h-7" data-testid="delete-floor-btn"><Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Delete Floor</Button>
          </div>
        </div>
      ) : (
        <div className="glass rounded-lg p-12 text-center">
          <ImagePlus className="w-12 h-12 mx-auto text-orange-400 mb-3" strokeWidth={2.5} />
          <p className="text-gray-500">No floor plans for this site yet. Click <b>Add Floor Plan</b> to upload a JPEG/PNG/PDF blueprint.</p>
        </div>
      )}

      {/* Add floor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Floor Plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Floor Number</Label><Input type="number" value={form.floor_number} onChange={(e) => setForm({ ...form, floor_number: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Ground Floor" /></div>
            <div className="col-span-2">
              <Label>Upload Blueprint (JPEG / PNG / PDF)</Label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                onClick={() => fileInputRef.current?.click()}
                className={`mt-1 rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition ${dragging ? "border-orange-500 bg-orange-500/5" : "border-white/10 bg-white/5 hover:border-orange-500/40"}`}
                data-testid="floor-dropzone"
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-orange-400" strokeWidth={2.5} />
                <div className="text-sm font-semibold">Drop file here or click to browse</div>
                <div className="text-xs text-gray-500 mt-1">JPEG, PNG, PDF · up to 10 MB · stored inline as base64</div>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="floor-file-input" />
              </div>
            </div>
            <div className="col-span-2"><Label className="text-xs text-gray-500">Or paste image URL</Label><Input value={form.image_url?.startsWith("data:") ? "" : form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="https://..." /></div>
            {form.image_url && !form.image_url.startsWith("data:application/pdf") && (
              <div className="col-span-2 rounded-lg overflow-hidden border border-gray-200">
                <img src={form.image_url} alt="" className="w-full max-h-60 object-contain bg-slate-100" />
              </div>
            )}
            {form.image_url?.startsWith("data:application/pdf") && (
              <div className="col-span-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 p-4 text-center text-xs text-gray-500">
                PDF ready to upload — preview will render after save.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={addFloor} className="f360-gradient border-0" data-testid="save-floor-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pin picker (now with equipment type filter) */}
      <Dialog open={showPinPicker} onOpenChange={setShowPinPicker}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader><DialogTitle>Place equipment pin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Equipment type</Label>
              <Select value={pinDeviceType} onValueChange={(v) => { setPinDeviceType(v); setPinDevice(""); }}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="pin-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
                  {Object.entries(EQUIPMENT).filter(([k]) => k !== "pump").map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bind to specific device</Label>
              <Select value={pinDevice} onValueChange={setPinDevice}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="pin-device-select"><SelectValue placeholder={devicesByType.length ? "Select device" : "No available devices of this type"} /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
                  {devicesByType.map((d) => <SelectItem key={d.device_id} value={d.device_id}>{d.device_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!devicesByType.length && <div className="text-[11px] text-amber-300 mt-1">No free {EQUIPMENT[pinDeviceType]?.label}s left. Add more devices in /devices.</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPinPicker(false)}>Cancel</Button>
            <Button onClick={savePin} disabled={!pinDevice} className="f360-gradient border-0" data-testid="save-pin-btn">Place Pin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pin detail drawer */}
      <Sheet open={!!activePin} onOpenChange={(o) => !o && setActivePin(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-md">
          {activePin && (
            <>
              <SheetHeader className="border-b border-gray-200 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: activePin.meta.color + "33" }}>
                    <activePin.meta.icon className="w-5 h-5" style={{ color: activePin.meta.color }} />
                  </span>
                  <div>
                    <Badge style={{ background: activePin.meta.color + "22", color: activePin.meta.color }} className="border-0 text-[10px]">{activePin.meta.label}</Badge>
                  </div>
                </div>
                <SheetTitle className="text-gray-900 text-xl">{activePin.device_name}</SheetTitle>
                <div className="text-xs text-gray-500">Device ID: <span className="font-mono text-orange-300">{activePin.device_id}</span></div>
              </SheetHeader>
              <div className="py-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Live Status</div>
                  <div className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${STATUS_BG[activePin.status.code]} ${activePin.status.code === "ok" ? "status-pulse-green" : activePin.status.code === "fault" ? "status-pulse-red" : ""}`}></span>
                    <div>
                      <div className="text-lg font-extrabold uppercase">{activePin.status.label}</div>
                      {activePin.status.value && <div className="text-xs text-gray-500 font-mono">{activePin.status.value}</div>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 rounded p-3"><div className="text-gray-500">Type</div><div className="font-bold mt-0.5">{activePin.device_type}</div></div>
                  <div className="bg-gray-50 rounded p-3"><div className="text-gray-500">Last sync</div><div className="font-bold mt-0.5">{tele?.updated_at ? new Date(tele.updated_at).toLocaleTimeString() : "—"}</div></div>
                </div>
                <a href={`/devices/${activePin.device_id}/history`} className="block text-center f360-gradient rounded-lg py-2.5 font-semibold">View Device History →</a>
                {editMode && (
                  <Button onClick={() => deletePin(activePin.idx)} variant="destructive" className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-300">
                    <Trash2 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Remove Pin
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
